import type * as React from "react";
import { cn } from "@/lib/utils";

/**
 * QL-119 — the geometry of one option row, for callers that cannot use
 * `OptionRow` itself because the row has to be a different element (e.g. a
 * native `<label>` wrapping its own `<input>`).
 *
 * The min-height *is* the rhythm between options: 44px on touch so every option
 * clears the tap-target floor, 32px from `md:` where a pointer is precise.
 */
export const optionRowClass = "group/option flex min-h-11 items-center gap-2 md:min-h-8";

/**
 * The hover affordance for a bare option control (radio, checkbox, switch).
 *
 * `group-hover/option` is the point of it: the whole row is one click target,
 * so hovering the words has to light up the control. A halo drawn with
 * `box-shadow` rather than `ring`, for two reasons — it stacks with the focus
 * ring instead of outranking it, and it sits behind the control's own fill, so
 * one rule covers checked, unchecked and indeterminate alike. The bare `hover:`
 * half keeps a control that is used outside `OptionRow` responsive on its own.
 */
export const optionHoverClass =
  "enabled:hover:shadow-[0_0_0_4px_hsl(var(--primary)/0.12)] enabled:group-hover/option:shadow-[0_0_0_4px_hsl(var(--primary)/0.12)]";

type OptionRowProps = React.ComponentPropsWithoutRef<"div"> & {
  /**
   * `center` (default) vertically centres the control in the row.
   * `start` lines the control up with the first text line — use it when the
   * option carries a helper line under its label, where centring would float
   * the control into the middle of two lines.
   */
  align?: "center" | "start";
};

/**
 * QL-119 — one row of a choice field (radio group, checkbox group, switch group).
 *
 * Because the row's min-height carries the rhythm, option lists get no
 * `space-y-*` of their own and `RadioGroup` defaults to `gap-0` — adding either
 * on top double-spaces the list.
 *
 * The row also stretches its own `<label>` so the whole row is the click area,
 * not just the words — in both directions. Horizontal comes from `grow`;
 * vertical needs `self-stretch`, because `items-center` otherwise sizes the
 * label to its one line of text and leaves the rest of the row's height (the
 * 44px floor, plus any padding a caller adds) hovering but not clicking. The
 * label becomes a flex box of its own only so its text stays centred once it
 * is taller than the text.
 */
export function OptionRow({ className, align = "center", ...props }: OptionRowProps) {
  return (
    <div
      className={cn(
        "group/option flex gap-2 [&>label]:grow [&>label]:cursor-pointer",
        align === "center"
          ? "min-h-11 items-center md:min-h-8 [&>label]:flex [&>label]:items-center [&>label]:self-stretch"
          : "items-start py-1.5",
        className,
      )}
      {...props}
    />
  );
}
